<template>
  <div class="creator-list">
    <div class="creator-header">
      <div class="creator-col">创作者</div>
      <div class="creator-col">智能体</div>
    </div>
    <div v-for="item in filteredCreators" :key="item.id" class="creator-item">
      <div class="creator-col creator-info">
        <div class="creator-avatar" :style="{ background: getAvatarColor(item) }">
          <img v-if="item.headImage" :src="item.headImage" />
          <span v-else>{{ (item.nickName || item.userName || '').charAt(0) }}</span>
        </div>
        <div class="creator-details">
          <div class="creator-name">{{ item.nickName || item.userName }}</div>
          <div class="creator-bio">{{ item.signature }}</div>
          <div class="creator-stats">
            <span class="creator-followers">👥 {{ item.followers }}</span>
            <span class="creator-tags">{{ (item.tags || []).map(t => '#' + t).join(' ') }}</span>
          </div>
        </div>
      </div>
      <div class="creator-col creator-agents">
        <div class="agents-container">
          <div v-for="agent in filterAgents(item.agents)" :key="agent.agentId" class="agent-card" :class="{ selected: selectedAgentId === agent.agentId }" @click.stop="goToAgentProfile(agent)" @mouseenter="showTooltip($event, agent)" @mouseleave="hideTooltip">
            <div class="selected-check" v-if="selectedAgentId === agent.agentId">✓</div>
            <div class="agent-avatar" :style="{ background: agent.avatarUrl ? 'transparent' : agent.color }">
              <img v-if="agent.avatarUrl" :src="agent.avatarUrl" />
              <span v-else>{{ agent.icon }}</span>
            </div>
            <div class="agent-name">{{ agent.name }}</div>
            <div class="agent-stats">
              <span class="subscribers">{{ formatSubscribers(agent.subscribers) }}人</span>
              <span class="price">{{ agent.price > 0 ? '¥' + agent.price : '免费' }}</span>
              <span class="access-scope">{{ formatAccessScope(agent.accessScope) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="loading" class="loading-state">加载中...</div>
    <div v-if="!loading && filteredCreators.length === 0" class="empty-state">{{ searchText ? '未找到匹配的创作者或智能体' : '暂无创作者' }}</div>
    <div v-if="tooltipVisible" class="agent-tooltip" :style="tooltipStyle" @mouseenter="keepTooltip" @mouseleave="hideTooltip">
      <div class="tooltip-desc">{{ tooltipAgent.description || '暂无描述' }}</div>
      <button class="use-btn" @click.stop="selectAgent(tooltipAgent)">立即使用</button>
    </div>
  </div>
</template>

<script>
import creatorApi from '@/api/creatorApi'

const AVATAR_COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2']

export default {
  name: 'CreatorContent',
  props: {
    searchText: { type: String, default: '' },
    selectedAgentId: { type: String, default: '' }
  },
  data() {
    return {
      creators: [],
      loading: false,
      tooltipVisible: false,
      tooltipAgent: {},
      tooltipStyle: {},
      tooltipTimer: null
    }
  },
  computed: {
    filteredCreators() {
      if (!this.searchText) return this.creators
      const kw = this.searchText.toLowerCase()
      return this.creators.filter(c => 
        (c.nickName || '').toLowerCase().includes(kw) ||
        (c.userName || '').toLowerCase().includes(kw) ||
        (c.agents || []).some(a => 
          (a.name || '').toLowerCase().includes(kw) || 
          (a.description || '').toLowerCase().includes(kw)
        )
      )
    }
  },
  mounted() {
    this.loadCreators()
  },
  methods: {
    async loadCreators() {
      this.loading = true
      try {
        const isLoggedIn = this.$store.getters.isLoggedIn
        const data = await creatorApi.getCreatorList(10, !isLoggedIn)
        this.creators = data || []
      } catch (e) {
        console.error('加载创作者失败:', e)
        this.creators = []
      } finally {
        this.loading = false
      }
    },
    filterAgents(agents) {
      if (!this.searchText) return agents || []
      const kw = this.searchText.toLowerCase()
      return (agents || []).filter(a => 
        (a.name || '').toLowerCase().includes(kw) || 
        (a.description || '').toLowerCase().includes(kw)
      )
    },
    selectAgent(agent) {
      this.hideTooltip()
      const isLoggedIn = this.$store.getters.isLoggedIn
      if (!isLoggedIn) {
        this.goToAgentProfile(agent)
      } else {
        this.$emit('select-agent', agent)
      }
    },
    showTooltip(e, agent) {
      clearTimeout(this.tooltipTimer)
      const rect = e.currentTarget.getBoundingClientRect()
      this.tooltipAgent = agent
      this.tooltipStyle = {
        left: rect.left + rect.width / 2 + 'px',
        top: rect.top - 8 + 'px',
        transform: 'translate(-50%, -100%)'
      }
      this.tooltipVisible = true
    },
    hideTooltip() {
      this.tooltipTimer = setTimeout(() => {
        this.tooltipVisible = false
      }, 100)
    },
    keepTooltip() {
      clearTimeout(this.tooltipTimer)
    },
    getAvatarColor(item) {
      return AVATAR_COLORS[item.id % AVATAR_COLORS.length]
    },
    goToAgentProfile(agent) {
      if (agent.agentId) {
        this.$router.push(`/agent/profile/a/${agent.agentId}`);
      }
    },
    formatSubscribers(count) {
      if (!count) return 0
      if (count >= 10000) return (count / 10000).toFixed(1) + '万'
      if (count >= 1000) return (count / 1000).toFixed(1) + 'k'
      return count
    },
    formatAccessScope(scope) {
      const map = { PUBLIC: '公开', FRIENDS: '好友', GROUP_MEMBERS: '群成员' }
      return map[scope] || '公开'
    }
  }
}
</script>

<style lang="scss" scoped>
.creator-list {
  background: white;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  position: relative;
}
.creator-header {
  display: grid;
  grid-template-columns: 180px 1fr;
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 2px solid #e5e5e5;
  font-weight: 600;
  color: #333;
  border-radius: 8px 8px 0 0;
}
.creator-item {
  display: grid;
  grid-template-columns: 180px 1fr;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  overflow: visible;
  position: relative;
  z-index: 2;
  &:last-child { border-bottom: none; }
  &:hover { background-color: #f8f9fa; z-index: 100; }
}
.creator-col { font-size: 14px; color: #666; }
.creator-info { display: flex; gap: 12px; align-items: flex-start; }
.creator-avatar {
  width: 48px; height: 48px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  color: white; font-size: 20px; font-weight: 600; flex-shrink: 0;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.creator-details { flex: 1; display: flex; flex-direction: column; gap: 4px; }
.creator-name { font-size: 16px; font-weight: 600; color: #333; text-align: left; }
.creator-bio { font-size: 13px; color: #666; }
.creator-stats { display: flex; gap: 12px; font-size: 12px; color: #999; }
.creator-followers { color: #666; }
.creator-tags { color: #667eea; }
.creator-agents { padding: 4px 0; overflow: visible; }
.agents-container {
  display: flex; gap: 8px; flex-wrap: wrap;
  padding: 8px; background: #f8f9fa; border-radius: 8px; border: 1px solid #e5e5e5;
  overflow: visible; position: relative;
}
.agent-card {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 6px 8px; background: white; border-radius: 6px; border: 1px solid #e5e5e5;
  transition: all 0.2s; cursor: pointer; position: relative;
  &:hover { transform: translateY(-2px); box-shadow: 0 2px 8px rgba(0,0,0,0.1); }
  &.selected {
    border-color: #667eea;
    box-shadow: 0 0 0 2px rgba(102,126,234,0.2);
  }
}
.selected-check {
  position: absolute; top: -8px; right: -8px;
  width: 20px; height: 20px; border-radius: 50%;
  background: #667eea; color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: bold; z-index: 10;
}
.agent-avatar {
  width: 36px !important; height: 36px !important; border-radius: 50%;
  display: flex; align-items: center; justify-content: center; font-size: 16px;
  overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
}
.agent-name { font-size: 11px; color: #666; white-space: nowrap; }
.agent-stats {
  display: flex; gap: 6px; font-size: 10px; color: #999;
  .subscribers { color: #666; }
  .price { color: #ff6b6b; font-weight: 500; }
  .access-scope { color: #52c41a; }
}
.agent-tooltip {
  display: flex; flex-direction: column; gap: 8px;
  position: fixed;
  background: white; padding: 12px; border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  min-width: 180px; max-width: 280px; z-index: 9999;
  border: 1px solid #e5e5e5;
  .tooltip-desc {
    font-size: 12px; color: #666; line-height: 1.5; text-align: left;
  }
  .use-btn {
    background: none; border: none; padding: 0;
    color: #667eea; font-size: 12px; cursor: pointer;
    text-align: right;
    &:hover { color: #764ba2; text-decoration: underline; }
  }
}
.loading-state, .empty-state { padding: 40px; text-align: center; color: #999; }
</style>
